import React, { useContext, useEffect, useState } from 'react';
import { Table, Typography, Tooltip, Row, Col,Button } from 'antd'
import { IntakeModal } from '../intakeModal/intakeModal.component';
import "./medicalInfo.scss";
import { UserContext } from '../../contexts/user.context.js';
import moment from 'moment'
import QuestionTemplateCode from '../../enums/questionTemplateCode.enum.js';
import { LockOutlined, FormOutlined } from '@ant-design/icons';
import { PageHeader } from '../pageHeader/pageHeader.component.js';
import { useParams } from 'react-router-dom';
import Role from '../../enums/role.enum.js';
import { listQuestions } from '../../services/question.service.js';
import { useNavigate } from 'react-router-dom';
import FlowType from '../../enums/flowType.enum.js';

const { Column } = Table
const { Text } = Typography

const WHITELIST = [
  // QuestionTemplateCode.GENDER,
  // QuestionTemplateCode.DOB,
  // QuestionTemplateCode.ETHNICITY,

  // Medical History
  QuestionTemplateCode.CURRENT_MEDICAL_CONDITIONS,
  QuestionTemplateCode.OTHER_CURRENT_MEDICAL_CONDITIONS,
  QuestionTemplateCode.ALL_MEDICAL_CONDITIONS,
  QuestionTemplateCode.ALLERGIES,
  QuestionTemplateCode.SURGERIES,
  QuestionTemplateCode.FREEFORM_MEDICATIONS,
  QuestionTemplateCode.FREEFORM_SUPPLEMENTS,
  QuestionTemplateCode.FAMILY_HISTORY,

  // Lifestyle
  QuestionTemplateCode.DIET,
  QuestionTemplateCode.PREVIOUS_SMOKING,
  QuestionTemplateCode.SMOKING,
  QuestionTemplateCode.EXERCISE,
  QuestionTemplateCode.STRENGTH,
  QuestionTemplateCode.SLEEP_HOURS,
  QuestionTemplateCode.SLEEP_QUALITY,
  QuestionTemplateCode.STRESS,
  QuestionTemplateCode.TRACKERS,
  QuestionTemplateCode.ALCOHOL_QTY,
  QuestionTemplateCode.MEDITATE,
  QuestionTemplateCode.PLANNING_CHILDREN,
  
  // Female only
  QuestionTemplateCode.PREGNANCY,
  QuestionTemplateCode.MENOPAUSE,
  QuestionTemplateCode.MENUSTRAL_CYCLE,

  // STAFF ONLY
  QuestionTemplateCode.BIRTH_CONTROL,
  QuestionTemplateCode.VERY_MUSCULAR,
  QuestionTemplateCode.RECENT_INJURY,
  QuestionTemplateCode.ALCOHOL,
  QuestionTemplateCode.LAST_PERIOD_DATE,
  QuestionTemplateCode.RECENT_VIRAL_INFECTION,
  QuestionTemplateCode.CURRENT_SUPPLEMENTS, 
  QuestionTemplateCode.CURRENT_MEDICATIONS,

]

export const MedicalInfo = () => {
  const { patientId } = useParams()
  const { currentUser, token } = useContext(UserContext)
  const [questions, setQuestions] = useState()
  const [questionId, setQuestionId] = useState()
  const [questionsForEveryone, setQuestionsForEveryone] = useState([])
  const [questionsForStaffOnly, setQuestionsForStaffOnly] = useState([])
  const navigate = useNavigate()


  useEffect(() => {
    document.title = 'Instalab | Health Profile'
  }, [])

  useEffect(() => {
    fetchQuestions()
  }, [patientId, currentUser])

  useEffect(() => {
    if (!questions) return;

    // Filter questions for everyone
    const filteredQuestionsForEveryone = questions.filter(
      ({ questionTemplate }) => !questionTemplate.isStaffOnly && WHITELIST.includes(questionTemplate.code)
    );
    setQuestionsForEveryone(filteredQuestionsForEveryone);

    // // Filter questions for staff only
    const filteredQuestionsForStaffOnly = questions.filter(
      ({ questionTemplate }) => questionTemplate.isStaffOnly && WHITELIST.includes(questionTemplate.code)
    );
    setQuestionsForStaffOnly(filteredQuestionsForStaffOnly);

  }, [questions]);

  const fetchQuestions = async () => {
    if (!currentUser) return
    const userId = currentUser.role === Role.PATIENT ? currentUser._id : patientId
    const response = await listQuestions({  
      filter: {
        patient: userId
      }
    })
    setQuestions(response)
  }

  const formatPatientValue = (question, value) => {
    const ConditionalList = ({ lines }) =>{

      if (!lines || lines.length===0 || lines[0]==="" || lines==="") return <>None</>
    
      // If it's a single line, return just the text
      if (lines.length === 1) { return <>{lines[0]}</> }
    
      const listItems = lines.map((item, index) => <li key={index}>{item}</li>);
      
      return <ul>{listItems}</ul>;
    }

    switch (typeof value) {
      case 'string':
        return <ConditionalList lines={value.split('\n')}/>
      case 'object':
        if (value?.length) {
          return <ConditionalList lines={value}/>
        } else {
          return 'None'
        }
      case 'boolean':
        if (value === true) return 'Yes'
        if (value === false) return 'No'
        return <Text className="not-available-text">N/A</Text>
      default:
        return <>[Fill In]</>
    }
  }

  const onFillOut = () => {
    navigate(`/flow/${FlowType.INTAKE}`)
  }

  const sortAnswers = (answers) => {
    return answers?.sort((a,b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }

  const getLatestAnswer = (answers) => {
    if (!answers?.length) return
    return sortAnswers(answers)[0].content
  }

  const getTooltipHistory = (hoverQuestionId) => {
    const hoverQuestion = questions.find(({ _id }) => _id === hoverQuestionId)
    if (!hoverQuestion) return
    const sortedAnswers = sortAnswers(hoverQuestion.answers)
    if (!sortedAnswers?.length) return
    return (
      <div className="history-container">
        {sortedAnswers.slice(0, 3).map(answer => (
          <Row 
            key={`tooltip-answer-${answer._id}`}
            className="history-tooltip-row"
          >
            <Col 
              style={{ width: 100 }}
              className="history-tooltip-col"
            >
              <b>{moment(answer.createdAt).format('MMM D, YYYY')}</b>
            </Col>
            <Col 
              style={{ width: 80 }}
              className="history-tooltip-col"
            >
              {moment(answer.createdAt).format('h:mm a')}
            </Col>
            <Col 
              style={{ width: 500 }}
              className="history-tooltip-col"
            >
              {formatPatientValue(hoverQuestion, answer.content)}
            </Col>
          </Row>
        ))}
      </div>
    )
  }

  const CustomTableRow = (props) => {
    
    const hoverQuestionId = props['data-row-key']
    const history = getTooltipHistory(hoverQuestionId)

    return (
      <Tooltip 
        title={(currentUser?.role === Role.ADMIN || currentUser?.role === Role.PROVIDER) && history}
        overlayStyle={{ maxWidth: 800 }}
        className="tooltip-style"
      >
        <tr {...props} />
      </Tooltip>
    );
  }

  const onIntakeSuccess = () => {
    fetchQuestions()
  }


  
  return questionsForEveryone && 
    (<>
    <div className="medical-info">
      <PageHeader 
        title='Health Profile' 
        actions={
          currentUser?.role === Role.PATIENT && (
          <Button
          type='primary'
          onClick={onFillOut}
        >
          <FormOutlined/> Update Health Profile
          
        </Button>
       )}/>

      {token && (<> 
        {/* Edit specific medical question */}
        <IntakeModal 
          questionId={questionId}
          setQuestionId={setQuestionId}
          autoOpen={false}
          hasFlow={questions?.some(({ isAnswered }) => !isAnswered)}
          onSuccess={onIntakeSuccess}
        />
        
        {/* Open automatically if there are unanswered questions  */}
        {/* <IntakeModal 
          autoOpen={true} 
          hasFlow={true}
        /> */}
      </>)}
      
     

      {questionsForEveryone && questionsForEveryone?.length > 0 && 
      <Table
        loading={!questionsForEveryone?.length}
        size='small'
        className='primary-table'
        pagination={false}
        rowKey='_id'
        dataSource={questionsForEveryone}
        showHeader={false}
        rowClassName={token && currentUser?.role === Role.ADMIN ? 'pointer-row' : 'default-row'}
        onRow={({ _id }) => {
          if (currentUser?.role === Role.ADMIN) {
            return {
              onClick: () => {
                setQuestionId(_id)
              }
            };
          }

          return null;
        }}
        components={{body: { row: CustomTableRow }}}

      >
        <Column dataIndex='content' />

        <Column
          dataIndex='answers'
          render={(answers, question) => formatPatientValue(question, getLatestAnswer(answers))}
          width={250} 
        />
      </Table>
      }

      {currentUser?.role === Role.ADMIN && questionsForStaffOnly && questionsForStaffOnly?.length > 0 && 
      <div className="staff-only-box">
        <div className="staff-only-box-header"><LockOutlined />{" "}For Staff Only</div>

        <Table
          loading={!questionsForStaffOnly?.length}
          size='small'
          className='primary-table'
          pagination={false}
          rowKey='_id'
          dataSource={questionsForStaffOnly}
          showHeader={false}
          rowClassName={token ? 'pointer-row' : 'default-row'}
          onRow={({ _id }) => {
            return {
              onClick: () => {
                setQuestionId(_id)
              }
            };
          }}
          components={{body: { row: CustomTableRow }}}
        >
          <Column dataIndex='content' />

          <Column
            dataIndex='answers'
            render={(answers, question) => formatPatientValue(question, getLatestAnswer(answers))}
            // width={250} 
          />
        </Table>
      </div>
      }
  

      
      
    </div>
  </>)
}